import { useState } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { Button, Typography } from '@getsynapse/design-system';
import { Request } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { getProjectRequestsStatus } from 'state/Project/projectSlice';
import RequestsDropdown from './RequestsDropdown/RequestsDropdown';
import LinkedProjectRequestsTable from './LinkedProjectRequestsTable/LinkedProjectRequestsTable';
import LinkedRequestsSkeletonLoader from './LinkedRequestsSkeletonLoader';

type ProjectRequestsProps = {
  projectId: string;
  linkedRequestsList: Request[];
  disabled?: boolean;
  onOpenLegacyRequest: (requestId: string) => void;
  onOpenAdvancedRequest: (requestId: string) => void;
};

const ProjectRequests: React.FC<ProjectRequestsProps> = ({
  linkedRequestsList = [],
  disabled = false,
  projectId,
  onOpenLegacyRequest,
  onOpenAdvancedRequest,
}) => {
  const projectRequestsStatus = useSelector(getProjectRequestsStatus);

  const [displayRequestsDropdown, setDisplayRequestsDropdown] = useState(false);

  const toggleRequestsDropdown = () =>
    setDisplayRequestsDropdown((prevState) => !prevState);

  const viewRequestHandle = (request: Request) => {
    if (!request.id) return;

    if (request.isJsonSchema) {
      onOpenAdvancedRequest(request.id);
    } else {
      onOpenLegacyRequest(request.id);
    }
  };

  return (
    <div className='mt-10'>
      <Typography variant='h5' className='mt-8 mb-4'>
        {intl.get('PROJECT_DETAIL.LINKED_REQUESTS_SECTION.TITLE')}
      </Typography>
      {projectRequestsStatus === SLICE_STATUS.LOADING && (
        <>
          <LinkedRequestsSkeletonLoader />
          <div className='animate-pulse h-9 bg-primary-lighter-two w-36 mt-5' />
        </>
      )}
      {projectRequestsStatus === SLICE_STATUS.IDLE && (
        <>
          <div className='pb-1 px-1'>
            <LinkedProjectRequestsTable
              requestsList={linkedRequestsList}
              projectId={projectId}
              viewRequest={viewRequestHandle}
              disabled={disabled}
            />
            {displayRequestsDropdown ? (
              <div className='flex items-center justify-between'>
                <div className='flex-1'>
                  <RequestsDropdown
                    onClose={() => setDisplayRequestsDropdown(false)}
                    linkedRequests={linkedRequestsList}
                    projectId={projectId}
                  />
                </div>
              </div>
            ) : (
              <Button
                onClick={toggleRequestsDropdown}
                iconName='add-circle'
                variant='tertiary'
                data-cy='request-search-projects-to-link'
                disabled={disabled}
              >
                {intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.LINK_REQUEST'
                )}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectRequests;
